import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAlarmCategoryColor } from "../../Utils/Utils";
import { openModal } from "./alarmsSlice";
import { calculateTimeDifference, getAlarmSeverityLabel, stringToColor } from "./alarmUtils";
import TableCellTooltip from "../../Components/Global Components/Table Cell Tooltip/TableCellTooltip";
import { Tooltip, Chip, Box, IconButton } from "@mui/material";
import {
  HistoryToggleOff as HistoryToggleOffIcon,
  CheckCircleOutline as AckIcon,
  CancelOutlined as ClearIcon,
  InfoOutlined as InfoIcon,
  AccessTime as DurationIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { trimAssetName } from "../../Common/EndPoints";
import { OverflowTip } from "../../Components/Mcdo/helpers";

export const ALARM_TABLE_INFO = {
  id: `alarms-table`,
  label: `Alarms`,
  requireAuthSign: false,
  timestamp: { startTs: null, endTs: null },
};



const AlarmsActionIcons = ({ params, allowUpdate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleAction = (type, alarmId) => dispatch(openModal({ modalType: type, alarmId: alarmId }));

  // show clear icon only if clear_dt is not present
  const showClearIcon = params.row.clear_dt ? false : true;
  const isAcked = !!params.row.ack_dt;

  function handleTimelineClick() {
    navigate(`/timeline?device_id=${params.row.device_id}&alarm_type=${params.row.type}`);
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {allowUpdate && (
        <>
          <Tooltip title={isAcked ? "Already Acknowledged" : "Acknowledge Alarm"} disableInteractive>
            <span>
              <IconButton
                size="small"
                disabled={isAcked}
                onClick={() => {
                  if (!isAcked) handleAction("ack", params.row.id);
                }}
                sx={{
                  color: isAcked ? 'var(--neutral-300)' : 'var(--success-main)',
                  '&:hover': {
                    backgroundColor: isAcked ? 'transparent' : 'var(--success-light)',
                  },
                }}
              >
                <AckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {showClearIcon && (
            <Tooltip title="Clear Alarm" disableInteractive>
              <IconButton
                size="small"
                onClick={() => handleAction("clear", params.row.id)}
                sx={{
                  color: 'var(--error-main)',
                  '&:hover': {
                    backgroundColor: 'var(--error-light)',
                  },
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
      <Tooltip title="View Timeline" disableInteractive>
        <IconButton
          size="small"
          onClick={handleTimelineClick}
          sx={{
            color: 'var(--primary-500)',
            '&:hover': {
              backgroundColor: 'var(--primary-50)',
            },
          }}
        >
          <HistoryToggleOffIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Alarm Details" disableInteractive>
        <IconButton
          size="small"
          onClick={() => handleAction("info", params.row.id)}
          sx={{
            color: 'var(--info-main, #3b82f6)',
            '&:hover': {
              backgroundColor: 'var(--info-light, #dbeafe)',
            },
          }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const AlarmsCategoryCell = ({ params }) => {
  const categoryValue = params.row.category;
  return (
    <Chip
      label={categoryValue || '—'}
      size="small"
      sx={{
        backgroundColor: 'var(--neutral-100)',
        color: getAlarmCategoryColor(categoryValue) || 'var(--neutral-600)',
        fontWeight: 500,
        fontSize: '0.75rem',
        height: '24px',
        border: `1px solid ${getAlarmCategoryColor(categoryValue) || 'var(--neutral-300)'}20`,
      }}
    />
  );
};
const getDeviceNameTrimmed = (params) => {
  var deviceName = params?.row?.device?.name;
  if (deviceName) {
    deviceName = trimAssetName(deviceName);
  }
  return deviceName;
};
const DeviceNameCell = ({ params }) => {
  var deviceName = getDeviceNameTrimmed(params);
  return <div>{deviceName}</div>;
};

const AlarmTotalActiveDuration = ({ params }) => {
  const fromTime = params.row.created_dt;
  const toTime = params.row.clear_dt ?? null;
  const [difference, setDifference] = useState(calculateTimeDifference(fromTime, toTime));
  useEffect(() => {
    const interval = setInterval(() => {
      const newDifference = calculateTimeDifference(fromTime, toTime);
      if (difference !== newDifference) {
        setDifference(newDifference);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [fromTime, toTime]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <DurationIcon sx={{ fontSize: 14, color: 'var(--neutral-400)' }} />
      <span style={{ fontWeight: 500, color: 'var(--neutral-700)', fontSize: '0.8125rem' }}>{difference}</span>
    </Box>
  );
};

// Status cell component with chip styling
const AlarmStatusCell = ({ params }) => {
  const isActive = params.row.is_active;
  return (
    <Chip
      label={isActive ? "ACTIVE" : "CLEARED"}
      size="small"
      sx={{
        backgroundColor: isActive ? 'var(--error-light)' : 'var(--success-light)',
        color: isActive ? 'var(--error-dark)' : 'var(--success-dark)',
        fontWeight: 600,
        fontSize: '0.6875rem',
        height: '22px',
        textTransform: 'uppercase',
        letterSpacing: '0.025em',
      }}
    />
  );
};

// Severity cell component with chip styling
const AlarmSeverityCell = ({ params }) => {
  const severity = params.row.severity;
  const label = getAlarmSeverityLabel(severity);

  const getSeverityStyles = () => {
    switch (severity) {
      case 'CRITICAL':
        return {
          backgroundColor: 'var(--alarm-failure-bg, #fee2e2)',
          color: 'var(--alarm-failure-text, #b80000)',
        };
      case 'WARNING':
        return {
          backgroundColor: 'var(--alarm-predictive-bg, #fef3c7)',
          color: 'var(--alarm-predictive-text, #b97400)',
        };
      case 'MAINTENANCE':
        return {
          backgroundColor: 'var(--alarm-overdue-bg, #e5d9f9)',
          color: 'var(--alarm-overdue-text, #5a459e)',
        };
      default:
        return {
          backgroundColor: 'var(--neutral-100)',
          color: 'var(--neutral-600)',
        };
    }
  };

  const styles = getSeverityStyles();

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        ...styles,
        fontWeight: 600,
        fontSize: '0.6875rem',
        height: '22px',
        textTransform: 'uppercase',
        letterSpacing: '0.025em',
      }}
    />
  );
};

export const ALARMS_COLUMNS = [
  {
    field: "editActions",
    type: "actions",
    headerName: "",
    width: 130,
    renderCell: (params) => <AlarmsActionIcons params={params} allowUpdate={true} />,
  },
  {
    field: "created_dt",
    headerName: "Alarm Time",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 155,
    hideable: true,
    // flex: 1,
    valueFormatter: (date) => moment(date.value).format("DD-MM-YYYY HH:mm:ss") ?? "-",
  },
  {
    field: "alarm_duration",
    headerName: "Duration",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 80,
    maxWidth: 80,
    hideable: true,
    flex: 1,
    renderCell: (params) => <AlarmTotalActiveDuration params={params} />,
  },
  {
    field: "device.software.code",
    headerName: "System",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 80,
    maxWidth: 80,
    hideable: true,
    flex: 1,
    renderCell: (params) => {
      return <TableCellTooltip textContent={params.row.device.software.code} />;
    },
  },
  {
    field: "clear_dt",
    headerName: "Corrected Time",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 155,
    hideable: true,
    flex: 1,
    valueFormatter: (date) => (date?.value ? moment(date.value).format("DD-MM-YYYY HH:mm:ss") ?? "-" : "-"),
  },
  {
    field: "ack_dt",
    headerName: "Ack Time",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 155,
    hideable: true,
    flex: 1,
    valueFormatter: (date) => (date?.value ? moment(date.value).format("DD-MM-YYYY HH:mm:ss") ?? "-" : "-"),
  },
  {
    field: "customer.station",
    headerName: "Station",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 70,
    maxWidth: 80,
    hideable: true,
    flex: 1,
    valueGetter: (params) => params?.row?.customer?.station ?? "-",
  },
  {
    field: "device.name",
    headerName: "Asset Name",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 150,
    hideable: true,
    flex: 1,
    renderCell: (params) => {
      const deviceName = getDeviceNameTrimmed(params);
      return <OverflowTip>{deviceName}</OverflowTip>
    },
    // valueFormatter: (params) => getDeviceNameTrimmed(params),
  },
  {
    field: "is_active",
    headerName: "Status",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 100,
    maxWidth: 110,
    hideable: true,
    flex: 1,
    renderCell: (params) => <AlarmStatusCell params={params} />,
  },
  {
    field: "severity",
    headerName: "Severity",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 150,
    maxWidth: 180,
    hideable: true,
    flex: 1,
    renderCell: (params) => <AlarmSeverityCell params={params} />,
  },
  {
    field: "type",
    headerName: "Alarm Type",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 150,
    hideable: true,
    flex: 1,
    renderCell: (params) => {
      return <TableCellTooltip textContent={params.value} />;
    },
  },
  // {
  //   field: "event_count",
  //   headerName: "Alarm Count",
  //   headerAlign: "center",
  //   align: "center",
  //   sortable: false,
  //   minWidth: 90,
  //   maxWidth: 100,
  //   hideable: true,
  //   flex: 1,
  // },
  {
    field: "category",
    headerName: "Category",
    headerAlign: "center",
    align: "center",
    sortable: false,
    minWidth: 100,
    hideable: true,
    flex: 1,
    renderCell: (params) => <AlarmsCategoryCell params={params} />,
  },
];
var ALARMS_COLUMNS_UPDATE = [...ALARMS_COLUMNS];
ALARMS_COLUMNS_UPDATE[0] = {
  field: "editActions",
  type: "actions",
  headerName: "",
  renderCell: (params) => <AlarmsActionIcons params={params} allowUpdate={false} />,
};
export const ALARMS_COLUMNS_NO_UPDATE = ALARMS_COLUMNS_UPDATE;

const _exportKeys = [];
const _exportAliases = [];
const _exportFormatter = [];
ALARMS_COLUMNS.forEach((column) => {
  //console.log("ALARMS_COLUMNS --> ", column.field);
  if (column.field !== "editActions" && column.field !== "alarm_duration") {
    _exportKeys.push(column.field);
    _exportAliases.push(column.headerName);
    if (column.field === "created_dt" || column.field === "clear_dt" || column.field === "ack_dt") {
      _exportFormatter.push("UTC");
      return;
    }
    if (column.field === "is_active") {
      _exportFormatter.push("alarm_status");
      return;
    }
    if (column.field === "device.name") {
      _exportFormatter.push("asset_name");
      return;
    }
    _exportFormatter.push("");
  }
});

export const ALARMS_EXPORT_PARAMS = {
  with_customer: true,
  with_device: true,
  with_device_type: true,
  export_keys: _exportKeys.join(","),
  export_aliases: _exportAliases.join(","),
  export_formatter: _exportFormatter.join(","),
  export: true,
};

const export_keys_alias_formatter = (columnVisibility) => {
  const _exportKeys = [];
  const _exportAliases = [];
  const _exportFormatter = [];
  ALARMS_COLUMNS.forEach((column) => {
    if (columnVisibility[column.field] ?? true) {
      if (column.field !== "editActions" && column.field !== "alarm_duration") {
        _exportKeys.push(column.field);
        _exportAliases.push(column.headerName);
        if (column.field === "created_dt" || column.field === "clear_dt" || column.field === "ack_dt") {
          _exportFormatter.push("UTC");
          return;
        }
        if (column.field === "is_active") {
          _exportFormatter.push("alarm_status");
          return;
        }
        _exportFormatter.push("");
      }
    }
  });
  return { _exportKeys, _exportAliases, _exportFormatter };
};

export const ALARMS_EXPORT_PARAMS_DYN = (columnVisibility) => {
  console.log(columnVisibility, "columnVisibility")
  return {
    with_customer: true,
    with_device: true,
    with_device_type: true,
    export_keys: export_keys_alias_formatter(columnVisibility)._exportKeys.join(","),
    export_aliases: export_keys_alias_formatter(columnVisibility)._exportAliases.join(","),
    export_formatter: export_keys_alias_formatter(columnVisibility)._exportFormatter.join(","),
    export: true,
  };
};
